'use client';

import { Button } from '@pickleballinc/react-ui';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Cross2Icon } from '@radix-ui/react-icons';
import * as React from 'react';

import { cn } from '../../lib/utils';

interface DialogProps extends React.ComponentProps<typeof DialogPrimitive.Root> {
	onClose?: () => void;
}

function Dialog({ onClose, onOpenChange, ...props }: DialogProps) {
	const handleOpenChange = (open: boolean) => {
		onOpenChange?.(open);
		if (!open && onClose) {
			onClose();
		}
	};

	return <DialogPrimitive.Root data-slot="dialog" onOpenChange={handleOpenChange} {...props} />;
}

function DialogTrigger({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
	return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
	return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({ ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) {
	return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({ className, children, ...props }: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
	return (
		<DialogPrimitive.Overlay
			data-slot="dialog-overlay"
			className={cn(
				'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 overflow-y-auto bg-black/50',
				className
			)}
			{...props}
		>
			{children}
		</DialogPrimitive.Overlay>
	);
}

function DialogContent({ className, children, ...props }: React.ComponentProps<typeof DialogPrimitive.Content>) {
	const childArray = React.Children.toArray(children);

	const hasBody = childArray.some((child) => React.isValidElement(child) && child.type === DialogBody);

	return (
		<DialogPortal data-slot="dialog-portal">
			<DialogOverlay>
				<div className="flex min-h-full items-center justify-center">
					<DialogPrimitive.Content
						data-slot="dialog-content"
						className={cn(
							'bg-white text-gray-900 border border-gray-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 relative z-50 grid w-full max-w-[calc(100%-2rem)] gap-4 rounded-lg p-6 shadow-lg duration-200 sm:max-w-lg',
							className,
							!hasBody && 'gap-8'
						)}
						{...props}
					>
						{children}
					</DialogPrimitive.Content>
				</div>
			</DialogOverlay>
		</DialogPortal>
	);
}

function DialogBody({ children, className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div data-slot="dialog-body" className={cn('flex flex-col items-start gap-2 w-full max-h-80 overflow-y-auto my-3', className)} {...props}>
			{children}
		</div>
	);
}

interface DialogHeaderProps extends React.ComponentProps<'div'> {
	inline?: boolean;
	showCloseButton?: boolean;
	centered?: boolean;
}

function DialogHeader({ children, inline = false, showCloseButton = true, centered = false, className, ...props }: DialogHeaderProps) {
	const childArray = React.Children.toArray(children);

	const title = childArray.find((child) => React.isValidElement(child) && child.type === DialogTitle);

	const subTitle = childArray.find((child) => React.isValidElement(child) && child.type === DialogSubTitle);

	const icon = childArray.find((child) => React.isValidElement(child) && child.type === DialogIcon);

	const hasIcon = !!icon;

	return (
		<div
			data-slot="dialog-header"
			className={cn(
				'flex flex-col items-center justify-between w-full',
				inline && 'flex-row items-start',
				hasIcon ? 'gap-4' : 'gap-0',
				showCloseButton && hasIcon && 'pt-3',
				className
			)}
			{...props}
		>
			{hasIcon ? (
				<>
					<div className={cn('flex items-center justify-between w-full', inline && 'w-fit')}>
						{icon}

						{showCloseButton && !inline ? (
							<DialogPrimitive.Close className="absolute right-6 top-6 rounded-sm opacity-70 ring-offset-white transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-gray-100 data-[state=open]:text-gray-600">
								<Cross2Icon className="size-6 text-gray-500" />
								<span className="sr-only">Close</span>
							</DialogPrimitive.Close>
						) : null}
					</div>
					<div className={cn('flex flex-col items-start gap-2 w-full', centered && 'items-center text-center')}>
						{title ? title : null}
						{subTitle ? subTitle : null}
					</div>
				</>
			) : (
				<div className="flex flex-row items-start justify-between w-full gap-4">
					<div className={cn('flex flex-col items-start gap-2 flex-1', centered && 'items-center text-center')}>
						{title ? title : null}
						{subTitle ? subTitle : null}
					</div>
					{showCloseButton && !inline ? (
						<DialogPrimitive.Close className="rounded-sm opacity-70 ring-offset-white transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-gray-100 data-[state=open]:text-gray-600 shrink-0">
							<Cross2Icon className="size-6 text-gray-500" />
							<span className="sr-only">Close</span>
						</DialogPrimitive.Close>
					) : null}
				</div>
			)}

			{showCloseButton && inline ? (
				<DialogPrimitive.Close className="rounded-sm opacity-70 ring-offset-white transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-gray-100 data-[state=open]:text-gray-600">
					<Cross2Icon className="size-6 text-gray-500" />
					<span className="sr-only">Close</span>
				</DialogPrimitive.Close>
			) : null}
		</div>
	);
}

function DialogAdditionalAction({ children, className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div data-slot="dialog-additional-action" className={cn('justify-center', className)} {...props}>
			{children}
		</div>
	);
}

function DialogCloseButton({ children, className, ...props }: React.ComponentProps<typeof Button>) {
	if (typeof children === 'string') {
		return (
			<DialogPrimitive.Close asChild>
				<Button data-slot="dialog-close-button" className={cn('justify-center w-full', className)} variant="secondary" size="lg" {...props}>
					{children}
				</Button>
			</DialogPrimitive.Close>
		);
	}

	if (React.isValidElement(children) && children.type === Button) {
		return <DialogPrimitive.Close asChild>{children}</DialogPrimitive.Close>;
	}

	return (
		<DialogPrimitive.Close asChild>
			<Button data-slot="dialog-close-button" className={cn('justify-center w-full', className)} variant="secondary" size="lg" {...props}>
				{children}
			</Button>
		</DialogPrimitive.Close>
	);
}

function DialogActionButton({ children, className, ...props }: React.ComponentProps<typeof Button>) {
	if (typeof children === 'string') {
		return (
			<Button data-slot="dialog-action-button" className={cn('justify-center w-full', className)} variant="primary" size="lg" {...props}>
				{children}
			</Button>
		);
	}

	if (React.isValidElement(children) && children.type === Button) {
		return children;
	}

	return (
		<Button data-slot="dialog-action-button" className={cn('justify-center w-full', className)} variant="primary" size="lg" {...props}>
			{children}
		</Button>
	);
}

function DialogFooter({ children, className, ...props }: React.ComponentProps<'div'>) {
	const childArray = React.Children.toArray(children);

	const additionalAction = childArray.find((child) => React.isValidElement(child) && child.type === DialogAdditionalAction);

	const closeButtonAction = childArray.find((child) => React.isValidElement(child) && child.type === DialogCloseButton);

	const actionButton = childArray.find((child) => React.isValidElement(child) && child.type === DialogActionButton);

	return (
		<div
			data-slot="dialog-footer"
			className={cn('flex items-center justify-center gap-2 w-full', additionalAction && 'justify-between', className)}
			{...props}
		>
			{additionalAction ? <div className="hidden sm:block flex-1 w-full">{additionalAction}</div> : null}
			<div className={cn('flex flex-col-reverse sm:flex-row items-center justify-center gap-2 w-full', additionalAction && 'w-fit')}>
				{closeButtonAction}
				{actionButton}
			</div>
		</div>
	);
}

function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
	return <DialogPrimitive.Title data-slot="dialog-title" className={cn('text-lg font-semibold text-gray-900', className)} {...props} />;
}

function DialogSubTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Description>) {
	return <DialogPrimitive.Description data-slot="dialog-description" className={cn('text-sm text-gray-600', className)} {...props} />;
}

interface DialogIconProps extends React.ComponentProps<'div'> {
	centered?: boolean;
}

function DialogIcon({ children, className, centered = false, ...props }: DialogIconProps) {
	return (
		<div data-slot="dialog-icon" className={cn('flex items-center justify-center', centered ? 'w-full' : 'w-fit', className)} {...props}>
			{children}
		</div>
	);
}

export {
	Dialog,
	DialogClose,
	DialogContent,
	DialogSubTitle,
	DialogFooter,
	DialogHeader,
	DialogOverlay,
	DialogPortal,
	DialogTitle,
	DialogTrigger,
	DialogIcon,
	DialogCloseButton,
	DialogAdditionalAction,
	DialogActionButton,
	DialogBody
};
